// AGC: Platform-aware share service. Native uses @capacitor/share + @capacitor/filesystem
// AGC: to emit a real Android ACTION_SEND intent with an attached file. Web falls back to
// AGC: navigator.share (Files API) then to a download anchor as last resort.
// AGC: Centralising this avoids the broken navigator.canShare({ files }) path inside the
// AGC: Capacitor WebView where it returns false and the <a download> link never triggers.

import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { Filesystem, Directory } from '@capacitor/filesystem'

export type ShareMime = 'application/pdf' | 'image/png' | 'text/plain'

export interface ShareFileInput {
  data: Blob | ArrayBuffer | Uint8Array
  fileName: string
  mimeType: ShareMime
}

export interface ShareRequest {
  title: string
  text?: string
  file?: ShareFileInput
  dialogTitle?: string
}

export type ShareResult =
  | { success: true; method: 'native' | 'web-share' | 'web-download' }
  | { success: false; cancelled: true; method: 'native' | 'web-share' }

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer()
  return bytesToBase64(new Uint8Array(buf))
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)))
  }
  return btoa(binary)
}

function toBlob(data: Blob | ArrayBuffer | Uint8Array, mime: string): Blob {
  if (data instanceof Blob) return data
  if (data instanceof Uint8Array) {
    return new Blob([data.slice().buffer as ArrayBuffer], { type: mime })
  }
  return new Blob([data], { type: mime })
}

async function writeNativeCacheFile(file: ShareFileInput): Promise<string> {
  const blob = toBlob(file.data, file.mimeType)
  const base64 = await blobToBase64(blob)
  const safeName = file.fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
  const result = await Filesystem.writeFile({
    path: safeName,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  })
  return result.uri
}

async function shareNative(req: ShareRequest): Promise<ShareResult> {
  const options: Parameters<typeof Share.share>[0] = {
    title: req.title,
    dialogTitle: req.dialogTitle ?? req.title,
  }
  if (req.text) options.text = req.text
  if (req.file) {
    const uri = await writeNativeCacheFile(req.file)
    options.files = [uri]
  }
  try {
    await Share.share(options)
    return { success: true, method: 'native' }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    if (/cancel/i.test(message)) return { success: false, cancelled: true, method: 'native' }
    throw err
  }
}

async function shareWeb(req: ShareRequest): Promise<ShareResult> {
  if (req.file && typeof navigator !== 'undefined' && navigator.share) {
    const blob = toBlob(req.file.data, req.file.mimeType)
    const webFile = new File([blob], req.file.fileName, { type: req.file.mimeType })
    const canShareFiles = typeof navigator.canShare === 'function'
      ? navigator.canShare({ files: [webFile] })
      : true
    if (canShareFiles) {
      try {
        await navigator.share({ title: req.title, text: req.text, files: [webFile] })
        return { success: true, method: 'web-share' }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (/abort|cancel/i.test(message)) return { success: false, cancelled: true, method: 'web-share' }
      }
    }
  }

  if (req.file) {
    const blob = toBlob(req.file.data, req.file.mimeType)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = req.file.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(url), 5_000)
    return { success: true, method: 'web-download' }
  }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: req.title, text: req.text })
      return { success: true, method: 'web-share' }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      if (/abort|cancel/i.test(message)) return { success: false, cancelled: true, method: 'web-share' }
      throw err
    }
  }

  if (req.text) {
    await navigator.clipboard.writeText(req.text)
    return { success: true, method: 'web-download' }
  }

  throw new Error('Sharing not supported on this platform')
}

export async function share(req: ShareRequest): Promise<ShareResult> {
  if (Capacitor.isNativePlatform()) return shareNative(req)
  return shareWeb(req)
}

export function isNativeShare(): boolean {
  return Capacitor.isNativePlatform()
}
